/**
 * One loading pattern for the whole app.
 *
 * Every page here is "fetch something, show a skeleton, then show it or say
 * why not", and writing that four times per page is how the four end up
 * behaving differently. `reload()` is the same call again — used after a
 * revoke, a rollback, or by the activity feed's poll.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { errorMessage } from "./api";

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  /** True only for the first load; a reload keeps the current data on screen. */
  loading: boolean;
  reloading: boolean;
  reload: () => Promise<void>;
  /** Applies a local change without a round trip (a revoked row, say). */
  patch: (update: (current: T) => T) => void;
}

export function useAsync<T>(load: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  // The loader is captured per render; keeping it in a ref lets `reload` stay
  // stable while still calling the current one.
  const loadRef = useRef(load);
  loadRef.current = load;
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async (signal: AbortSignal, first: boolean) => {
    if (first) setLoading(true);
    else setReloading(true);
    try {
      const result = await loadRef.current(signal);
      if (signal.aborted || !mounted.current) return;
      setData(result);
      setError(null);
    } catch (cause) {
      if (signal.aborted || !mounted.current) return;
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(errorMessage(cause));
    } finally {
      if (!signal.aborted && mounted.current) {
        setLoading(false);
        setReloading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void run(controller.signal, true);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const reload = useCallback(async () => {
    const controller = new AbortController();
    await run(controller.signal, false);
  }, [run]);

  const patch = useCallback((update: (current: T) => T) => {
    setData((current) => (current === null ? current : update(current)));
  }, []);

  return { data, error, loading, reloading, reload, patch };
}

/** Calls `tick` every `intervalMs` while the tab is visible. Used by the activity feed. */
export function usePolling(tick: () => void, intervalMs: number, enabled = true): void {
  const tickRef = useRef(tick);
  tickRef.current = tick;
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => {
      // Polling a hidden tab spends the user's battery to refresh something
      // nobody is looking at.
      if (document.visibilityState === "visible") tickRef.current();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs, enabled]);
}
